import { Component, OnInit, OnDestroy, Input, EventEmitter, Output, AfterViewInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { Company } from '@models/company';
import { DataViewColumn, DataViewRow } from '@models/dataview';
import { Select2OptionData } from '@components/shared/select2/select2.interface';
import { DataFrameService } from '@services/dataframes.service';
import { AlertService } from '@services/alert.service';
import { NgxPopupService } from '@components/shared/ngx-popups/ngx-popups/ngx-popups';
import {
  DATE_FORMAT,
  SERVER_DATE_FORMAT,
  TAXONOMY_ITEM_IDS,
  STD_ID_MAPPER,
  SUPPORTED_CURRENCIES
} from '@utils/constants';
import moment from 'moment';
import { GrowthType, NormalBalance } from '@utils/enums';
import { NumberFormattingService } from '@services/number-formatting.service';
import { LaunchDarklyService } from '@services/launchdarkly.service';
import { ConfirmationPopupComponent } from '@components/shared/popups/confirmation/confirmation-popup.component';
import { finalize } from 'rxjs/operators';

declare var bootstrap: any;

@Component({
    selector: 'app-generate-forecast-form',
    templateUrl: './generate-forecast-form.component.html',
    styleUrls: ['./generate-forecast-form.component.scss'],
    standalone: false
})
export class GenerateForecastFormComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() company: Company;
  @Input() columns: DataViewColumn[];
  @Input() rows: DataViewRow[];
  @Input() spreadingTemplateItems: any[];
  @Input() frameToBeEditedId?: number;
  @Input() isDuplicateMode = false;
  @Output() closeGeneratePeriods = new EventEmitter<boolean>();

  private readonly subs = new Subscription();
  isSubmitting = false;
  isLoading = false;
  showPrefillReferencePeriodNotFoundError = false;
  hasAttemptedSubmit = false;
  MAX_SCENARIO_NAME_LENGTH = 200;

  /** Form-backed values (single source of truth) - use getters for reads */
  get statementDate(): string | null {
    return this.forecastForm?.get('statementDate')?.value ?? null;
  }
  get reportingInterval(): string {
    return this.forecastForm?.get('reportingInterval')?.value ?? '';
  }
  get projectionName(): string {
    return this.forecastForm?.get('projectionName')?.value ?? '';
  }
  get selectedReferenceFrameIndex(): number {
    const val = this.forecastForm?.get('selectedReferenceFrameIndex')?.value;
    return val === undefined || val === null || val === '' ? -1 : Number(val);
  }
  get scenarioType(): string | null {
    return this.forecastForm?.get('scenarioType')?.value ?? null;
  }

  /** FormArray of line items - single source of truth for growth mappings */
  get lineItemsArray(): FormArray {
    return this.forecastForm?.get('lineItems') as FormArray;
  }

  reportingIntervalOptions: Select2OptionData[] = [
    {
      id: 'MONTHLY',
      text: 'MONTHLY'
    },
    {
      id: 'QUARTERLY',
      text: 'QUARTERLY'
    },
    {
      id: 'SEMI_ANNUALLY',
      text: 'SEMI_ANNUALLY'
    },
    {
      id: 'ANNUALLY',
      text: 'ANNUALLY'
    },
    {
      id: 'FISCAL_YTD',
      text: 'FISCAL_YTD'
    },
    {
      id: 'TTM',
      text: 'TTM'
    }
  ];

  growthTypeOptions = [
    {
      id: GrowthType.Factor,
      text: "%"
    },
    {
      id: GrowthType.Fixed,
      text: "$"
    },
    {
      id: GrowthType.ExactValue,
      text: "#"
    }
  ];

  periodOptions: Select2OptionData[] = [];
  taxonomyItemOptions: Select2OptionData[] = [];
  /** Cached taxonomy options per line item index (excludes ids selected in other rows). Updated when line items or selection change. */
  lineItemTaxonomyOptions: Select2OptionData[][] = [];

  statementDateHasBeenChangedByUser = false;
  hasReferenceFrameBeenChangedByUser = false;

  taxonomyItemIdToValueMappingsFromReferencePeriod: Record<string, { value: number; normalBalance: NormalBalance }> = {};

  currency: string;
  supportedCurrencies = SUPPORTED_CURRENCIES;


  // should recalculate check everytime
  // a new reference sheet is selected
  // a new line item is assigned a taxonomy item
  // a line item growth type is updated
  // a line item value is updated
  // a line item is deleted
  check = 0;

  // we will allow user to complete forecast if check balance is between -1 and 1, may want to make this configurable later
  readonly CHECK_THRESHOLD = 1

  // this is kind of a silly variable name. But heres the deal
  // 1. we SHOULD check the balance sheet balance if a balance sheet exists for the reference frame (because otherwise there is nothing to balance)
  // 2. we CAN deterministically figure out the balance if the following Taxonomy items are found (REF_TOTAL_ASSETS, REF_TOTAL_LIABILITIES, and REF_TOTAL_EQUITY)
  // ^ if one of those doesnt exist, it is not possible to check the balance and therefore we'll just ignore the validation
  shouldAndCanCheckBalanceSheetBalance = false;
  shouldForceForecastsToBeBalanced = true;

  static readonly NUMERIC_PATTERN = /^[-+]?(\d+(\.\d*)?|\.\d+)([eE][-+]?\d+)?$/;
  WHOLE_POSITIVE_INTEGER_PATTERN = /^\d+$/;

  readonly GROWTH_TYPE_TO_VALUE_PLACEHOLDER_TEXT_MAPPINGS: Record<string, string> = {
    FACTOR: 'Percent growth/decay',
    FIXED: 'Dollar growth/decay',
    EXACT_VALUE: 'Enter exact value',
  };

  itemIdsAssociatedWithAdjustableItems: string[] = [];

  scenarioTypeOptions = [
    { id: 'PROJECTION', text: 'Projection' },
    { id: 'PROFORMA', text: 'Proforma' }
  ];

  /** Static map for field error messages*/
  private static readonly FIELD_ERROR_MESSAGES: Record<string, string> = {
    selectedReferenceFrameIndex: 'Please select a Reference Period.',
    reportingInterval: 'Please select a Reporting Interval.',
    statementDate: 'Please select a Statement Date.',
    scenarioType: 'Please select a Scenario Type.',
  };

  forecastForm: FormGroup;

  constructor(
    private _dataFrameService: DataFrameService,
    private _alertService: AlertService,
    private _launchDarklyService: LaunchDarklyService,
    private popupService: NgxPopupService,
    public numberFormattingService: NumberFormattingService,
    private _formBuilder: FormBuilder,
  ) { }

  static referenceFrameRequired(control: AbstractControl): { [key: string]: boolean } | null {
    const val = control.value;
    if (val === -1 || val === undefined || val === null || val === '') {
      return { required: true };
    }
    return null;
  }

  static noWhitespaceOnly(control: AbstractControl): { [key: string]: boolean } | null {
    if (control.value && !control.value.trim()) {
      return { nospace: true };
    }
    return null;
  }

  /** Validator: value must be empty, '-' (typing), or match numeric pattern */
  static numericGrowthValue(control: AbstractControl): { [key: string]: boolean } | null {
    const v = control.value;
    if (v == null || v === '' || v === '-') { return null; }
    if (GenerateForecastFormComponent.NUMERIC_PATTERN.test(String(v))) { return null; }
    return { numeric: true };
  }

  /** Validator: when taxonomy item is adjustable, only Factor growth type is allowed */
  private adjustableItemGrowthTypeValidator(group: AbstractControl): { [key: string]: boolean } | null {
    const id = group.get('selectedTaxonomyItemId')?.value;
    const growthType = group.get('growthType')?.value;
    if (id && this.itemIdsAssociatedWithAdjustableItems.includes(id) &&
        (growthType === GrowthType.Fixed || growthType === GrowthType.ExactValue)) {
      return { adjustableItemRequiresFactor: true };
    }
    return null;
  }

  ngOnInit(): void {
    this.buildForecastForm();
    this.getFeatureFlags();
    this.columns.forEach((column, colIdx) => {
      const periodLabel = (column.statementDate + " - "  + column.preparationType + " - " + column.reportingInterval + (column.projectionName ? " - " + column.projectionName : ""));
      this.periodOptions.push({
        id: String(colIdx),
        text: periodLabel,
      })
    })
    this.addSpreadingTemplateItemsToTaxonomyItemOptions(this.spreadingTemplateItems);

    if (this.frameToBeEditedId){
      this._prefillEditForm()
    } else {
      this.currency = this.company.settings['currency'];
      this.addLineItem();
    }
  }

  private buildForecastForm(): void {
    this.forecastForm = this._formBuilder.group({
      selectedReferenceFrameIndex: [-1, [GenerateForecastFormComponent.referenceFrameRequired]],
      reportingInterval: ['', Validators.required],
      statementDate: [null, Validators.required],
      scenarioType: [null, Validators.required],
      projectionName: ['', [
        Validators.required,
        GenerateForecastFormComponent.noWhitespaceOnly,
        Validators.maxLength(this.MAX_SCENARIO_NAME_LENGTH)
      ]],
      lineItems: this._formBuilder.array([]),
    });
  }

  private createLineItemGroup(data?: { selectedTaxonomyItemId?: string; growthType?: string; growthValue?: string | null }): FormGroup {
    const group = this._formBuilder.group({
      selectedTaxonomyItemId: [data?.selectedTaxonomyItemId ?? '', [
        Validators.required,
        Validators.pattern(this.WHOLE_POSITIVE_INTEGER_PATTERN)
      ]],
      growthType: [data?.growthType ?? GrowthType.Factor, Validators.required],
      growthValue: [data?.growthValue ?? null, [
        Validators.required,
        GenerateForecastFormComponent.numericGrowthValue
      ]],
    });
    group.setValidators(() => this.adjustableItemGrowthTypeValidator(group));
    return group;
  }

  lineItemTrackByIndex(_: number, index: number): number {
    return index;
  }

  /** Recomputes lineItemTaxonomyOptions from current line items. Call when line items or selection change. */
  private refreshLineItemTaxonomyOptions(): void {
    const controls = this.lineItemsArray?.controls ?? [];
    this.lineItemTaxonomyOptions = controls.map((_, index) => {
      const selectedIds = controls
        .map((c, i) => (i !== index ? c.get('selectedTaxonomyItemId')?.value : null))
        .filter(Boolean);
      return this.taxonomyItemOptions.filter(opt => !selectedIds.includes(opt.id));
    });
  }

  ngAfterViewInit() {
    this.enableTooltips()
  }

  enableTooltips() {
    // Bootstrap tooltip initialization
    const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
    tooltipTriggerList.forEach(tooltipTriggerEl => {
      return new bootstrap.Tooltip(tooltipTriggerEl)
    })
  }

  _prefillEditForm() {
    this.isLoading = true
    this._dataFrameService.loadPrefillDataForForecastedFrame(this.frameToBeEditedId)
      .pipe(finalize(() => {this.isLoading = false}))
      .subscribe(({statementBuilderRawFormInput, basePeriodFrame}) => {
        const projectionName = this.isDuplicateMode
          ? (statementBuilderRawFormInput.projectionName ? `Copy of ${statementBuilderRawFormInput.projectionName}` : 'Copy of Scenario')
          : statementBuilderRawFormInput.projectionName;
        this.forecastForm.patchValue({
          projectionName,
          reportingInterval: statementBuilderRawFormInput.reportingInterval,
          statementDate: statementBuilderRawFormInput.statementDate,
          scenarioType: statementBuilderRawFormInput.scenarioType || 'PROJECTION',
        }, { emitEvent: false });
        this.currency = statementBuilderRawFormInput.currency;
        this._prefillSelectedReferenceFrame(statementBuilderRawFormInput.selectedReferenceFramePeriodKeyUuid, basePeriodFrame);
        this._prefillLineItemRows(statementBuilderRawFormInput.lineItems);
        setTimeout(() => {
          this.setBalanceSheetCheck();
        }, 0);
      }, error => {
        console.error('failed to load prefill values: ', error);
      })
  }

  _prefillSelectedReferenceFrame(selectedReferenceFramePeriodKeyUuid, basePeriodFrame) {
    let findMatchingFrame = (col) => col.periodKeyUuid === selectedReferenceFramePeriodKeyUuid
    if (basePeriodFrame?.projectionName) {
      findMatchingFrame = (col) => col.periodKeyUuid === selectedReferenceFramePeriodKeyUuid && col.projectionName === basePeriodFrame.projectionName
    }
    const refColIndex = this.columns.findIndex(findMatchingFrame);
    if (refColIndex === -1) {
     this.handleReferencePeriodNotFoundError();
    } else {
      this.forecastForm.patchValue({ selectedReferenceFrameIndex: refColIndex }, { emitEvent: false });
      this.updateAutofilledNewPeriodData();
      this.updateTaxonomyItemIdToValueMappingsFromReferencePeriod();
      this.setBalanceSheetCheck();
    }
  }
  
  handleReferencePeriodNotFoundError() {
    this.showPrefillReferencePeriodNotFoundError = true; 
  }

  _prefillLineItemRows(lineItems: any[]) {
    const lineItemsArray = this.lineItemsArray;
    while (lineItemsArray.length > 0) {
      lineItemsArray.removeAt(0);
    }
    lineItems.forEach(item => {
      lineItemsArray.push(this.createLineItemGroup({
        selectedTaxonomyItemId: String(item.taxonomyItemId),
        growthType: item.growthType,
        growthValue: item.amountValue != null ? String(item.amountValue) : null,
      }));
    });
    this.refreshLineItemTaxonomyOptions();
    this.setBalanceSheetCheck();
  }

  private getFeatureFlags(): void {
    this.shouldForceForecastsToBeBalanced = this._launchDarklyService.flags['force-forecasts-to-be-balanced'];
    this.subs.add(
      this._launchDarklyService.flagChange.subscribe((flags) => {
        this.shouldForceForecastsToBeBalanced = flags['force-forecasts-to-be-balanced'];
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  addSpreadingTemplateItemsToTaxonomyItemOptions(spreadingTemplateItems) {
    for (const spreadingTemplateItem of spreadingTemplateItems) {
      if (spreadingTemplateItem.children && spreadingTemplateItem.children.length > 0) {
        this.addSpreadingTemplateItemsToTaxonomyItemOptions(spreadingTemplateItem.children);
      } else if (!spreadingTemplateItem.hasRollup && ['StandardItem', 'SubItem', 'GenericStandardItem'].includes(spreadingTemplateItem.className)) {
        this.taxonomyItemOptions.push({
          id: String(spreadingTemplateItem.standardLineItemId),
          text: spreadingTemplateItem.label
        })
      } else if (spreadingTemplateItem.className === 'AdjustedStandardItem') {
          this.itemIdsAssociatedWithAdjustableItems.push(('' + spreadingTemplateItem.derivedFromItemId));
      }
    }
  }

  addLineItem(): void {
    this.lineItemsArray.push(this.createLineItemGroup());
    this.refreshLineItemTaxonomyOptions();
    this.setBalanceSheetCheck();
  }

  removeLineItem(index: number): void {
    this.lineItemsArray.removeAt(index);
    this.refreshLineItemTaxonomyOptions();
    this.setBalanceSheetCheck();
  }

  updateSelectedTaxonomyItemId(value: string, index: number): void {
    this.lineItemsArray.at(index).patchValue({ selectedTaxonomyItemId: value }, { emitEvent: false });
    this.lineItemsArray.at(index).updateValueAndValidity();
    this.refreshLineItemTaxonomyOptions();
    this.setBalanceSheetCheck();
  }

  updateGrowthType(growthType: string, index: number): void {
    this.lineItemsArray.at(index).patchValue({ growthType }, { emitEvent: false });
    this.lineItemsArray.at(index).updateValueAndValidity();
    this.setBalanceSheetCheck();
  }

  updateGrowthValue(growthValue: string, index: number): void {
    const growthValueControl = this.lineItemsArray.at(index).get('growthValue');
    this.lineItemsArray.at(index).patchValue({ growthValue: growthValue || null }, { emitEvent: false });
    growthValueControl?.updateValueAndValidity();
    growthValueControl?.markAsDirty();
    growthValueControl?.markAsTouched();
    this.setBalanceSheetCheck();
  }

  updateReportingInterval(reportingInterval: string) {
    this.forecastForm?.patchValue({ reportingInterval }, { emitEvent: false });
  }

  updateStatementDate(event: { dateObject?: { formatted?: string } }) {
    const formatted = event?.dateObject?.formatted;
    if (formatted != null) {
      this.forecastForm?.patchValue({ statementDate: formatted }, { emitEvent: false });
      this.forecastForm?.get('statementDate')?.updateValueAndValidity();
    }
    this.statementDateHasBeenChangedByUser = true;
  }

  updateProjectionName(projectionName: string) {
    this.forecastForm?.patchValue({ projectionName }, { emitEvent: false });
  }

  updateSelectedReferenceFrameIndex(selectedReferenceFrameIndex: string) {
    this.hasReferenceFrameBeenChangedByUser = true;
    this.forecastForm?.patchValue({ selectedReferenceFrameIndex: Number(selectedReferenceFrameIndex) }, { emitEvent: false });
    this.updateAutofilledNewPeriodData();
    this.updateTaxonomyItemIdToValueMappingsFromReferencePeriod();
    this.setBalanceSheetCheck();
  }

  updateScenarioType(scenarioType: string) {
    this.forecastForm?.patchValue({ scenarioType }, { emitEvent: false });
  }

  triggerCloseGeneratePeriods(newStatementCreated=false) {
    this.closeGeneratePeriods.emit(newStatementCreated);
  }

  isFieldInvalid(fieldName: string): boolean {
    const control = this.forecastForm?.get(fieldName);
    if (!control) { return false; }
    return (control.invalid && control.dirty) || (control.invalid && this.hasAttemptedSubmit);
  }

  /**
   * Returns the error message for a field when it should be shown (invalid and dirty or submit attempted).
   */
  getFieldError(fieldName: string): string | null {
    const control = this.forecastForm?.get(fieldName);
    if (!control?.invalid) { return null; }
    const shouldShow = control.dirty || this.hasAttemptedSubmit;
    if (!shouldShow) { return null; }
    const errors = control.errors ?? {};
    if (fieldName === 'projectionName') {
      if (errors['maxlength']) { return 'Scenario name cannot exceed 200 characters.'; }
      if (errors['required'] || errors['nospace']) { return 'Please enter a Scenario Name.'; }
      return null;
    }
    return GenerateForecastFormComponent.FIELD_ERROR_MESSAGES[fieldName] ?? null;
  }

  /**
   * Returns the error message for a line item control when it should be shown..
   */
  getLineItemError(index: number, controlName: string): string | null {
    const control = this.lineItemsArray?.at(index)?.get(controlName);
    if (!control?.invalid) { return null; }
    if (!(control.dirty || this.hasAttemptedSubmit)) { return null; }
    const errors = control.errors ?? {};
    if (controlName === 'selectedTaxonomyItemId') { return 'Please select a Line Item.'; }
    if (controlName === 'growthValue') {
      if (errors['required']) { return 'Please enter an amount.'; }
      if (errors['numeric']) { return 'Please enter only numerical values.'; }
      return null;
    }
    return null;
  }

  lineItemsHaveErrors(): boolean {
    return !!(this.lineItemsArray?.invalid);
  }

  isLineItemControlInvalid(index: number, controlName: string): boolean {
    const control = this.lineItemsArray?.at(index)?.get(controlName);
    if (!control) { return false; }
    return (control.invalid && control.dirty) || (control.invalid && this.hasAttemptedSubmit);
  }

  getLineItemGrowthTypeInvalid(index: number): boolean {
    const group = this.lineItemsArray?.at(index);
    if (!group) { return false; }
    return !!(((group.invalid && group.dirty) || (group.invalid && this.hasAttemptedSubmit)) &&
      group.errors?.['adjustableItemRequiresFactor']);
  }

  get hasFormErrors(): boolean {
    return this.hasAttemptedSubmit && (
      (this.forecastForm?.invalid ?? false) ||
      this.lineItemsHaveErrors()
    );
  }

  generateForecast() {
    this.hasAttemptedSubmit = true;
    if (this.forecastForm?.invalid || this.lineItemsHaveErrors()) {
      return;
    }
    // show warning/confirmation modal if the balance sheet is not balanced
    // if we force it to be balanced, this warning will never appear bc the balance must be within the threshold before button is enabled
    if (!(this.check < this.CHECK_THRESHOLD && this.check > -this.CHECK_THRESHOLD)) {
      this.popupService.open({
        componentType: ConfirmationPopupComponent,
        cssClass: 'modal-confirmation',
        inputs: {
          question: 'GENERATE FORECAST',
          text: 'The balance sheet is not balanced. Are you sure you want to generate this forecast?',
          confirmButtonText: 'Generate Forecast'
        },
        outputs: {
          callback: this.handleGenerateForecast.bind(this)
        },
      });
    } else {
      this.handleGenerateForecast(true);
    }
  }

  handleGenerateForecast(approved: boolean) {
    if (approved) {
      const projectionName = this.projectionName;
      if (projectionName && projectionName.length > this.MAX_SCENARIO_NAME_LENGTH) {
        this.hasAttemptedSubmit = true;
        return;
      }
      this.isSubmitting = true;
      let submitRequest$: Observable<any>;
      const sharedRequestArgs: [number, string, string, string, any, any, string, string, any] = [
        this.company.id,
        moment(this.statementDate, DATE_FORMAT).format(SERVER_DATE_FORMAT),
        this.reportingInterval,
        this.projectionName,
        this.colToFrameKey(this.columns[this.selectedReferenceFrameIndex]),
        this.transformTaxonomyItemIdToGrowthRateMappings(),
        this.currency,
        this.scenarioType,
        this._buildFormSnapshot(),
      ];
      if (this.isDuplicateMode) {
        // Duplicate mode: create a new forecasted frame using generateForecast
        submitRequest$ = this._dataFrameService.generateForecast(...sharedRequestArgs);
      } else if (this.frameToBeEditedId){
        submitRequest$ = this._dataFrameService.updateForecast(this.frameToBeEditedId, ...sharedRequestArgs);
      } else {
        submitRequest$ = this._dataFrameService.generateForecast(...sharedRequestArgs)
      }
      this.subs.add(
        submitRequest$.subscribe({
          next: () => {
            this._alertService.success('Statement Built! If the new statement isn\'t shown, please reload the page.');
            this.triggerCloseGeneratePeriods(true);
            this.isSubmitting = false;
          },
          error: (err) => {
            const errStr = JSON.stringify(err?.error || err?.message || err);
            if (errStr.includes('projection_name') && errStr.includes('200')) {
              this.hasAttemptedSubmit = true;
            } else {
              this._alertService.error(err?.message ?? 'An error occurred');
            }
            this.isSubmitting = false;
          },
        })
      );
    }
  }

  _buildFormSnapshot() {
    const idx = this.selectedReferenceFrameIndex;
    const lineItems = (this.lineItemsArray?.value ?? []).map((row: { selectedTaxonomyItemId: string; growthType: string; growthValue: string | null }) => ({
      taxonomyItemId: row.selectedTaxonomyItemId,
      growthType: row.growthType,
      amountValue: row.growthValue,
    }));
    return {
      scenarioType: this.scenarioType,
      projectionName: this.projectionName,
      reportingInterval: this.reportingInterval,
      statementDate: this.statementDate,
      selectedReferenceFramePeriodKeyUuid: this.columns[idx]?.periodKeyUuid,
      lineItems,
      currency: this.currency
    };
  }

  transformTaxonomyItemIdToGrowthRateMappings(): Record<string, { growth_type: string; value: number }> {
    const result: Record<string, { growth_type: string; value: number }> = {};
    const controls = this.lineItemsArray?.controls ?? [];
    for (const control of controls) {
      const id = control.get('selectedTaxonomyItemId')?.value;
      const growthType = control.get('growthType')?.value;
      let val = Number(control.get('growthValue')?.value);
      if (id == null || id === '' || !this.WHOLE_POSITIVE_INTEGER_PATTERN.test(id)) { continue; }
      if (growthType === GrowthType.Factor) {
        val = val / 100;
      }
      result[id] = {
        growth_type: growthType === GrowthType.ExactValue ? '' : growthType,
        value: val,
      };
    }
    return result;
  }

  colToFrameKey(column: DataViewColumn) {
    return {
      scenario: column.scenario,
      statement_date: moment(column.statementDate, DATE_FORMAT).format(SERVER_DATE_FORMAT),
      reporting_interval: column.reportingInterval,
      preparation_type: column.preparationType,
      projection_name: column.projectionName,
    }
  }

  updateAutofilledNewPeriodData() {
    if (!this.statementDateHasBeenChangedByUser && this.hasReferenceFrameBeenChangedByUser) {
      const refColumn = this.columns[this.selectedReferenceFrameIndex];
      if (refColumn?.statementDate) {
        this.forecastForm?.patchValue({ statementDate: refColumn.statementDate }, { emitEvent: false });
        this.forecastForm?.get('statementDate')?.updateValueAndValidity();
      }
    }
  }

  updateTaxonomyItemIdToValueMappingsFromReferencePeriod() {
    this.taxonomyItemIdToValueMappingsFromReferencePeriod = {};
    const referenceColumn = this.columns[this.selectedReferenceFrameIndex];
    const mapping = this.taxonomyItemIdToValueMappingsFromReferencePeriod;

    let balanceSheetSubsectionId = null

    this.rows.forEach((row, idx) => {
      // Source-item rows (shown via the Source Items toggle) aren't tied to a template lineItem.
      if (!row.lineItem) {
        return;
      }
      if (row.lineItem.id === STD_ID_MAPPER.REF_TOTAL_ASSETS) {
        balanceSheetSubsectionId = row.lineItem.id;
      } else if (row.lineItem.id === STD_ID_MAPPER.REF_TOTAL_LIABILITIES) {
        balanceSheetSubsectionId = row.lineItem.id;
      } else if (row.lineItem.id === STD_ID_MAPPER.REF_TOTAL_EQUITY) {
        balanceSheetSubsectionId = row.lineItem.id;
      } else if ([TAXONOMY_ITEM_IDS.CASH_FLOW_STATEMENT, TAXONOMY_ITEM_IDS.INCOME_STATEMENT, TAXONOMY_ITEM_IDS.UCA_CASHFLOW_DIRECT, TAXONOMY_ITEM_IDS.UCA_CASHFLOW_INDIRECT].includes(row.lineItem.id)) {
        balanceSheetSubsectionId = null;
      }

      // Balance Sheet section header row (when present in dataview): required for setBalanceSheetCheck.
      if (row.lineItem.id === TAXONOMY_ITEM_IDS.BALANCE_SHEET) {
        const value = referenceColumn.cells[idx]?.calculatedValue?.value ?? 0;
        mapping[String(TAXONOMY_ITEM_IDS.BALANCE_SHEET)] = { value, normalBalance: NormalBalance.Debit };
        return;
      }

      let rollupBehavior: NormalBalance | null = null;
      if (balanceSheetSubsectionId !== null) {
        if (balanceSheetSubsectionId === STD_ID_MAPPER.REF_TOTAL_ASSETS) {
          rollupBehavior = row.rollupBehavior === '-' ? NormalBalance.Credit : NormalBalance.Debit;
        } else {
          rollupBehavior = row.rollupBehavior === '-' ? NormalBalance.Debit : NormalBalance.Credit;
        }
      }

      if (rollupBehavior !== null) {
        this.taxonomyItemIdToValueMappingsFromReferencePeriod[String(row.lineItem.id)] = {
          value: referenceColumn.cells[idx].calculatedValue !== null ? referenceColumn.cells[idx].calculatedValue.value : 0,
          normalBalance: rollupBehavior
        };
      }
    })
  }

  setBalanceSheetCheck() {
    // first step to setting balance sheet check, is to get the balance before any line item growth has been applied
    // so search for total assets, total liabilities and total equity in the reference sheet and calculate the check from the
    let check = 0;
    if (
      this.taxonomyItemIdToValueMappingsFromReferencePeriod.hasOwnProperty(String(TAXONOMY_ITEM_IDS.BALANCE_SHEET)) &&
      this.taxonomyItemIdToValueMappingsFromReferencePeriod.hasOwnProperty(String(STD_ID_MAPPER.REF_TOTAL_ASSETS)) &&
      this.taxonomyItemIdToValueMappingsFromReferencePeriod.hasOwnProperty(String(STD_ID_MAPPER.REF_TOTAL_LIABILITIES)) &&
      this.taxonomyItemIdToValueMappingsFromReferencePeriod.hasOwnProperty(String(STD_ID_MAPPER.REF_TOTAL_EQUITY))
    ) {
      this.shouldAndCanCheckBalanceSheetBalance = true;
      check = this.taxonomyItemIdToValueMappingsFromReferencePeriod[String(STD_ID_MAPPER.REF_TOTAL_ASSETS)].value -
      this.taxonomyItemIdToValueMappingsFromReferencePeriod[String(STD_ID_MAPPER.REF_TOTAL_LIABILITIES)].value -
      this.taxonomyItemIdToValueMappingsFromReferencePeriod[String(STD_ID_MAPPER.REF_TOTAL_EQUITY)].value;
    } else {
      this.shouldAndCanCheckBalanceSheetBalance = false;
      return;
    }

    // iterate through line items to calc the updated check balance after growth has been applied
    const lineItemsArray = this.lineItemsArray;
    if (lineItemsArray) {
      for (const control of lineItemsArray.controls) {
        const taxonomyItemId = control.get('selectedTaxonomyItemId')?.value;
        const growthType = control.get('growthType')?.value;
        const value = control.get('growthValue')?.value;
        const valueControl = control.get('growthValue');
        const isValueValid = valueControl?.valid && value != null && value !== '';

        if (
          taxonomyItemId &&
          this.taxonomyItemIdToValueMappingsFromReferencePeriod.hasOwnProperty(taxonomyItemId) &&
          value !== null &&
          value !== '' &&
          isValueValid
        ) {
          const itemValueFromReferenceFrame = this.taxonomyItemIdToValueMappingsFromReferencePeriod[taxonomyItemId].value;
          let updatedVal = 0;
          if (growthType === GrowthType.Fixed) {
            updatedVal = itemValueFromReferenceFrame + Number(value);
          } else if (growthType === GrowthType.Factor) {
            updatedVal = itemValueFromReferenceFrame * (1 + (Number(value) / 100));
          } else {
            updatedVal = Number(value);
          }

          // determine if row has a natural debit or credit balance
          // if it has a natural debit balance, add the diff between the updatedVal and itemValueFromReferenceFrame
          // if it has a natural credit balance, subtract the diff between the updatedVal and itemValueFromReferenceFrame
          const changeFromReferencePeriod = (updatedVal - itemValueFromReferenceFrame);
          if (this.taxonomyItemIdToValueMappingsFromReferencePeriod[taxonomyItemId].normalBalance === NormalBalance.Debit) {
            check += changeFromReferencePeriod;
          } else {
            check -= changeFromReferencePeriod;
          }
        }
      }
    }

    // finally, update the check balance
    this.check = check;
  }

  copyCheckToClipboard() {
    navigator.clipboard.writeText(String(this.check)).then(() => {
      this._alertService.success('Copied balance sheet check', "Value Copied", 2)
    }, (error) => {
      console.error('Async: Could not copy text: ', error);
    });
  }

  changeCurrency(currency: string) {
    this.currency = currency;
  }

}
